import React, { useCallback, useEffect, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { ChevronDown, Link2, Undo2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { OfficialAccommodationLookup } from './OfficialAccommodationLookup';
import type { PreRegisteredAccommodationFormValues } from '../schemas/pre-registered-accommodation.schema';
import type { LinkedOfficialAccommodation } from '../types/official-accommodation.types';

interface RepatMatchesBlockProps {
  departmentId?: number;
  linked: LinkedOfficialAccommodation | null;
  onLinkedChange: (linked: LinkedOfficialAccommodation | null) => void;
  // Create saves the row already linked; edit links it right after saving the data
  mode: 'create' | 'edit';
}

const LinkedNote: React.FC<{
  linked: LinkedOfficialAccommodation;
  mode: RepatMatchesBlockProps['mode'];
  onUndo: () => void;
}> = ({ linked, mode, onUndo }) => (
  <div className="flex flex-col gap-2 rounded-md border border-green-300 bg-green-50 p-3 text-sm sm:flex-row sm:items-start sm:justify-between">
    <div className="space-y-1">
      <p className="font-medium text-green-900">
        <Link2 className="mr-1 inline h-4 w-4" />
        Se {mode === 'create' ? 'cargará' : 'guardará'} como <em>Ya registrado</em>, vinculado al
        legajo {linked.fileNumber} — {linked.establishmentName ?? 'Sin denominación'}.
      </p>
      <p className="text-green-800">
        Una fila en "Ya registrado" ya no se puede editar: para corregir sus datos hay que volverla a
        "Pendiente de control".
      </p>
    </div>
    <Button type="button" variant="outline" size="sm" className="shrink-0" onClick={onUndo}>
      <Undo2 className="mr-1 h-4 w-4" />
      Deshacer
    </Button>
  </div>
);

// Live matches in the official registry while the row's trade name is typed (never blocks saving)
export const RepatMatchesBlock: React.FC<RepatMatchesBlockProps> = ({
  departmentId,
  linked,
  onLinkedChange,
  mode,
}) => {
  const establishmentName = useWatch<PreRegisteredAccommodationFormValues, 'establishmentName'>({
    name: 'establishmentName',
  });
  const [query, setQuery] = useState(establishmentName ?? '');
  // The search follows the trade name until the user edits it by hand
  const [followsName, setFollowsName] = useState(true);
  const [open, setOpen] = useState(true);
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (followsName) setQuery(establishmentName ?? '');
  }, [establishmentName, followsName]);

  const changeQuery = (value: string) => {
    setFollowsName(false);
    setQuery(value);
  };

  const handleMatchesChange = useCallback((value: number) => setCount(value), []);

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="rounded-md border bg-gray-50">
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center justify-between px-3 py-2 text-left text-sm font-medium text-gray-800"
        >
          <span>Posibles coincidencias en REPAT ({count})</span>
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent className="space-y-3 border-t px-3 py-3">
        {linked && <LinkedNote linked={linked} mode={mode} onUndo={() => onLinkedChange(null)} />}
        {!followsName && (
          <button
            type="button"
            className="text-xs text-blue-700 underline-offset-2 hover:underline"
            onClick={() => setFollowsName(true)}
          >
            Volver a buscar por la denominación
          </button>
        )}
        <OfficialAccommodationLookup
          id="repat-matches-search"
          query={query}
          onQueryChange={changeQuery}
          departmentId={departmentId}
          selectedId={linked?.id}
          // Clicking the selected match again removes the link
          onSelect={({ id, fileNumber, establishmentName: name }) =>
            onLinkedChange(id === linked?.id ? null : { id, fileNumber, establishmentName: name })
          }
          actionLabel="Vincular"
          onMatchesChange={handleMatchesChange}
        />
      </CollapsibleContent>
    </Collapsible>
  );
};
